import produce from "immer";
import React, { JSX } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";

import { resourceCentreActions as actions } from "../../../../actions";
import { useAppDispatch } from "../../../../store/hooks";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import {
  OpdOrderFormsSettings as IOpdOrderFormsSettings,
  ResourceCentre
} from "../../../../interfaces/ResourceCentreInterface";

interface Props {
  resourceCentre?: ResourceCentre;
  onChange?: (rc: ResourceCentre) => void;
}

export default function OpdOrderFormsSettings({
  resourceCentre: propRc,
  onChange: propOnChange
}: Props = {}): JSX.Element {
  const dispatch = useAppDispatch();
  const currentRc = useCurrentResourceCentre();
  const resourceCentre = propRc ?? currentRc;

  const labOrdersShow = resourceCentre?.settings?.opdOrderFormsSettings?.labOrders?.show ?? true;
  const dentalOrdersShow =
    resourceCentre?.settings?.opdOrderFormsSettings?.dentalOrders?.show ?? true;
  const glassOrdersShow =
    resourceCentre?.settings?.opdOrderFormsSettings?.glassOrders?.show ?? true;

  const handleToggle = (key: keyof IOpdOrderFormsSettings): void => {
    if (!resourceCentre) {
      return;
    }

    const newRc = produce(resourceCentre, (draft) => {
      if (!draft.settings.opdOrderFormsSettings) {
        draft.settings.opdOrderFormsSettings = {
          dentalOrders: { show: true },
          glassOrders: { show: true },
          labOrders: { show: true }
        };
      }
      if (!draft.settings.opdOrderFormsSettings.dentalOrders) {
        draft.settings.opdOrderFormsSettings.dentalOrders = { show: true };
      }
      if (!draft.settings.opdOrderFormsSettings.glassOrders) {
        draft.settings.opdOrderFormsSettings.glassOrders = { show: true };
      }
      if (!draft.settings.opdOrderFormsSettings.labOrders) {
        draft.settings.opdOrderFormsSettings.labOrders = { show: true };
      }

      const currentVal = draft.settings.opdOrderFormsSettings[key]?.show ?? true;
      draft.settings.opdOrderFormsSettings[key].show = !currentVal;
    });

    if (propOnChange) {
      propOnChange(newRc);
    }
    dispatch(actions.putResourceCentre(newRc));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Show/Hide Order Forms</Typography>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={labOrdersShow}
              onChange={() => handleToggle("labOrders")}
            />
          }
          label="Order Forms for Laboratory"
        />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dentalOrdersShow}
              onChange={() => handleToggle("dentalOrders")}
            />
          }
          label="Dental Lab Orders"
        />
      </Box>

      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={glassOrdersShow}
              onChange={() => handleToggle("glassOrders")}
            />
          }
          label="Glass Orders"
        />
      </Box>
    </Box>
  );
}

export { OpdOrderFormsSettings };
